import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import api from '../../services/api';
import { Subscription, ApiResponse } from '../../types';

interface SubscriptionsState {
  subscriptions: Subscription[];
  currentSubscription: Subscription | null;
  loading: boolean;
  error: string | null;
  totalCount: number;
  currentPage: number;
  pageSize: number;
}

const initialState: SubscriptionsState = {
  subscriptions: [],
  currentSubscription: null,
  loading: false,
  error: null,
  totalCount: 0,
  currentPage: 1,
  pageSize: 20,
};

export const fetchSubscriptions = createAsyncThunk(
  'subscriptions/fetchSubscriptions',
  async ({ page = 1, pageSize = 20, filters }: { page?: number; pageSize?: number; filters?: any }) => {
    const params = new URLSearchParams({
      page: page.toString(),
      pageSize: pageSize.toString(),
      ...filters,
    });
    const response = await api.get<ApiResponse<Subscription[]>>(
      `/subscriptions?${params}`
    );
    return { 
      subscriptions: response.data.data, 
      total: response.data.pagination?.total || 0,
      page, 
      pageSize 
    };
  }
);


export const createSubscription = createAsyncThunk(
  'subscriptions/createSubscription',
  async (subscriptionData: Partial<Subscription>) => {
    const response = await api.post<ApiResponse<any>>('/subscriptions', subscriptionData);
    return response.data.data; // Contains redirectUrl, tokenId, etc.
  }
);

export const cancelSubscription = createAsyncThunk(
  'subscriptions/cancelSubscription',
  async (subscriptionId: string) => {
    const response = await api.post<ApiResponse<Subscription>>(`/subscriptions/${subscriptionId}/cancel`);
    return response.data.data;
  }
);

const subscriptionsSlice = createSlice({
  name: 'subscriptions',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearCurrentSubscription: (state) => {
      state.currentSubscription = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch subscriptions
      .addCase(fetchSubscriptions.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSubscriptions.fulfilled, (state, action) => {
        state.loading = false;
        state.subscriptions = action.payload.subscriptions;
        state.totalCount = action.payload.total;
        state.currentPage = action.payload.page;
        state.pageSize = action.payload.pageSize;
      })
      .addCase(fetchSubscriptions.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch subscriptions';
      })
      // Create subscription
      .addCase(createSubscription.fulfilled, (state, action: PayloadAction<Subscription>) => {
        state.subscriptions.unshift(action.payload);
      })
      // Cancel subscription
      .addCase(cancelSubscription.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(cancelSubscription.fulfilled, (state, action: PayloadAction<Subscription>) => {
        state.loading = false;
        const index = state.subscriptions.findIndex(sub => sub.id === action.payload.id);
        if (index !== -1) {
          state.subscriptions[index] = action.payload;
        }
      })
      .addCase(cancelSubscription.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to cancel subscription';
      });
  },
});

export const { clearError, clearCurrentSubscription } = subscriptionsSlice.actions;
export default subscriptionsSlice.reducer;